import { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";

interface Review {
  name: string;
  rating: number;
  comment: string;
  avatar?: string;
  image?: string;
}

interface ReviewsCarouselPreviewProps {
  settings: {
    title: string;
    reviews: Review[];
    autoplay: boolean;
    interval: number;
    showArrows: boolean;
    showDots: boolean;
    cardsPerView: number;
    cardStyle: string;
    backgroundColor: string;
    cardBackgroundColor: string;
    titleColor: string;
    textColor: string;
    starColor: string;
  };
}

export const ReviewsCarouselPreview = ({ settings }: ReviewsCarouselPreviewProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const {
    title,
    reviews,
    autoplay,
    interval,
    showArrows,
    showDots,
    cardStyle,
    backgroundColor,
    cardBackgroundColor,
    titleColor,
    textColor,
    starColor,
  } = settings;

  useEffect(() => {
    if (!autoplay || !reviews?.length) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoplay, interval, reviews?.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  if (!reviews?.length) {
    return (
      <div className="bg-muted p-12 text-center rounded-lg">
        <p className="text-muted-foreground">أضف تقييمات</p>
      </div>
    );
  }

  const currentReview = reviews[currentIndex];

  return (
    <div className="p-6" style={{ backgroundColor }}>
      <h3 className="text-2xl font-bold mb-6 text-center" style={{ color: titleColor }}>
        {title}
      </h3>

      <div className="relative max-w-2xl mx-auto">
        <div
          className={`p-6 rounded-xl ${cardStyle === "modern" ? "shadow-lg" : "border"}`}
          style={{ backgroundColor: cardBackgroundColor }}
        >
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {currentReview.avatar ? (
                <img 
                  src={currentReview.avatar} 
                  alt={currentReview.name} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="text-2xl font-bold">{currentReview.name[0]}</span>
              )}
            </div>
            <div className="flex-1">
              <h4 className="font-bold text-lg mb-1" style={{ color: textColor }}>
                {currentReview.name}
              </h4>
              <div className="flex gap-1 mb-2">
                {Array.from({ length: currentReview.rating }).map((_, i) => (
                  <Star key={i} className="h-5 w-5 fill-current" style={{ color: starColor }} />
                ))}
              </div>
            </div>
          </div>

          <p className="text-base leading-relaxed mb-4" style={{ color: textColor }}>
            {currentReview.comment}
          </p>

          {currentReview.image && (
            <img 
              src={currentReview.image} 
              alt="Review" 
              className="w-full rounded-lg"
              loading="lazy"
              decoding="async"
            />
          )}
        </div>

        {showArrows && reviews.length > 1 && (
          <>
            <button
              onClick={goToPrevious}
              className="absolute -left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 shadow-lg p-2 rounded-full transition"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={goToNext}
              className="absolute -right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-50 shadow-lg p-2 rounded-full transition"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {showDots && reviews.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {reviews.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition ${
                  currentIndex === index ? "bg-primary w-6" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
